import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IUserShort } from "../../../interfaces/interfaces";
import {
  HorizontalDivider,
  TextPlain,
  TitleH1,
  TitleH14,
  TitleH4
} from "../../../components/generic";
import { arrayLanguages, getNameCountry } from "../../../utils/fetchMapInfo";
import { activeYears } from "../../../utils/helpers";
import { FlexBox, OverviewInfoContainer } from "../../../components/containers";
import { TLang } from "../../../assets/data/isoNameLanguage";
import {
  CharacteristicsIcon,
  LanguagesIcon
} from "../../../components/Icons/studentOverview";
import { FlagIcon } from "../../../components/Icons/Flags";

import { Avatar, AvatarContainer } from "./styles";
import { InfoWithIconWrapper } from "../styles";

export const AboutStudent: FC<{
  data: Partial<IUserShort>;
  children?: never;
}> = ({
  data: {
    first_name,
    last_name,
    birth_date,
    house_rents_count,
    thumbnail_url,
    gender,
    birth_country,
    active_years,
    description,
    spoken_languages,
    categorization
  }
}) => {
  const [previewImageUrl, setPreviewImageUrl] = useState(thumbnail_url);
  const { t, i18n } = useTranslation("matches");
  const curLang: TLang = (i18n.language.toUpperCase() as TLang) ?? "GB";

  const [name, age, countTimes] = useMemo(() => {
    return [
      `${first_name ?? ""} ${last_name ?? ""}`,
      new Date().getFullYear() -
        new Date(birth_date ?? new Date()).getFullYear(),
      house_rents_count ?? 0
    ];
  }, [first_name, last_name, birth_date, house_rents_count]);

  const birthCountry = useMemo(() => {
    return getNameCountry(birth_country || "", curLang);
  }, [birth_country, curLang]);

  return (
    <OverviewInfoContainer maxWidth="500px" flexBasis="42%" margin="0 40px 0 0">
      <FlexBox margin="0 0 2rem" justify="flex-start" wrap="nowrap">
        <AvatarContainer>
          <Avatar
            src={thumbnail_url}
            width={100}
            height={100}
            alt="Student"
            onError={() => setPreviewImageUrl(undefined)}
            preview={
              typeof previewImageUrl === "string"
                ? {
                    src: previewImageUrl
                  }
                : false
            }
          />
        </AvatarContainer>
        <div>
          <TitleH1
            m="0 0 8px"
            fontSize="sizeBanner"
            lineHeight="40px"
            fontWeight="weightMedium"
            color="hardGray"
          >
            {name}
          </TitleH1>
          <TextPlain key="age and gender" color="hardGray" margin="0 0 0.5rem">
            {age}, {t(`host.userInfo.gender.${gender}`)}
          </TextPlain>
          {typeof birth_country === "string" && birth_country.length > 0 && (
            <InfoWithIconWrapper key="country" margin={0} isCentered>
              <FlagIcon
                countryCode={birth_country}
                curLang={curLang}
                margin="0 0.5rem 0 0"
              />
              <TextPlain key="age and gender" color="hardGray">
                {t(`host.userInfo.from`)} {birthCountry}
              </TextPlain>
            </InfoWithIconWrapper>
          )}
        </div>
      </FlexBox>
      <TitleH14 color="midGrey" m="0 0 0.5rem" fontWeight="weightNormal">
        {activeYears(active_years ?? 0) === "1" &&
          t(`host.userInfo.LessThan`) +
            " " +
            activeYears(active_years ?? 0)}{" "}
        {t(`host.userInfo.yearsActive`)}
      </TitleH14>
      <TitleH14 color="midGrey" m="0 0 2rem" fontWeight="weightNormal">
        {countTimes === 0 && t(`host.userInfo.roomsRented.0`)}
        {countTimes > 0 && (
          <>
            {" "}
            <strong>{countTimes}</strong>{" "}
            {t(`host.userInfo.roomsRented.${countTimes === 1 ? 1 : 2}`)}
          </>
        )}
      </TitleH14>
      <HorizontalDivider margin="0 0 2rem" />
      <TitleH4 m="0 0 8px" fW="weightMedium" c="hardGray">
        {t(`host.userInfo.descriptionTitle`)} {first_name ?? ""}
      </TitleH4>
      <TextPlain color="hardGray" lineHeight="2rem" margin="0 0 2rem">
        {description}
      </TextPlain>
      <TitleH4 m="0 0 0.5rem" fW="weightMedium" c="hardGray">
        {t(`host.userInfo.languages`)}
      </TitleH4>
      <InfoWithIconWrapper isCentered>
        <LanguagesIcon />
        <p>
          {spoken_languages && spoken_languages.length > 0
            ? arrayLanguages(spoken_languages, curLang).map((item, idx) => (
                <span key={item}>
                  {item}
                  {idx !== spoken_languages!.length - 1 && ", "}
                </span>
              ))
            : ""}
        </p>
      </InfoWithIconWrapper>
      {categorization && categorization.length > 0 && (
        <>
          <TitleH4 m="0 0 8px" fW="weightMedium" c="hardGray">
            {t(`host.userInfo.characteristics`)}
          </TitleH4>
          <InfoWithIconWrapper isCentered>
            <CharacteristicsIcon />
            <p>
              {categorization.map((item, idx) => (
                <span key={item}>
                  {t(`host.userInfo.categorization.${item}`)}
                  {idx !== categorization!.length - 1 && ", "}
                </span>
              ))}
            </p>
          </InfoWithIconWrapper>
        </>
      )}
    </OverviewInfoContainer>
  );
};
